import { useForm } from "react-hook-form";
import useQuotes from "../../data/useQuotes";
import Modal from "../../layouts/Modal";
import { TextInput } from "../shared/Inputs/TextInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { BodyCondition, EngineCondition } from "../../models/Quote";
import { useEffect, useState } from "react";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import openInNewTab from "../../utils/openInNewTab";
import useQuoteCalculate from "../../data/useQuoteCalculate";

export default function GenerateQuoteModal({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const { calculate } = useQuoteCalculate();
  const { register, getValues, setValue, watch, handleSubmit} = useForm({
    defaultValues: {
      vrm: "",
      postcode: "",
      voucherCode: "",
      engineCondition: EngineCondition.NoFault,
      bodyCondition: BodyCondition.Good, 
      alloyWheels: false,
      mileage: 0,
    }
  });

  const onSubmit = (formData: any) => {
    setLoading(true);
    console.log("Form Data", formData);
    const data = {
      vrm: formData.vrm,
      postcode: formData.postcode,
      voucherCode: formData.voucherCode,
      engineCondition: formData.engineCondition,
      bodyCondition: formData.bodyCondition,
      alloyWheels: formData.alloyWheels,
      mileage: formData.mileage ? parseInt(formData.mileage) : 0, // Ensure mileage is a number
    };

    calculate.mutate(data, {
      onSuccess: (response) => {
        console.log("Quote calculated successfully:", response);
        setLoading(false);
        setOpen(false); // Close modal on success
        openInNewTab(`abandoned-quotes/${response.data.id}`);
      },
      onError: (error) => {
        console.error("Error calculating quote:", error);
      }
    });
  }

  useEffect(() => {
    // Reset form values when modal opens
    if (open) {
      setValue("vrm", "");
      setValue("postcode", "");
      setValue("voucherCode", "");
      setValue("engineCondition", EngineCondition.NoFault);
      setValue("bodyCondition", BodyCondition.Good);
      setValue("alloyWheels", false);
      setValue("mileage", 0);
    }
  }, [open])

  if (loading) {
    return (
      <Modal width="max-w-xl" open={open} setOpen={() => {}}>
        <LoadingWheel />
      </Modal>
    );
  }

  return (
    <Modal width="max-w-xl" open={open} setOpen={loading ? () => {} : setOpen}>
      <h1 className="text-xl mb-4">{`Calculate New Quote`}</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">

        <TextInput
          label="Vrm"
          placeholder="Enter vrm"
          register={register}
          registerName={"vrm"}
        />
        <TextInput
          label="Postcode"
          placeholder="Enter the postcode"
          register={register}
          registerName={"postcode"}
        />

        <div className="col-span-1 sm:col-span-2">
          <TextInput
            label="Voucher Code"
            placeholder="Enter the voucher code (optional)"
            register={register}
            registerName={"voucherCode"}
          />
        </div>

        <SelectInput
          label="Engine Condition"
          register={register}
          registerName={"engineCondition"}
          options={[
            { id: EngineCondition.NoFault   , name: "No Fault" },
            { id: EngineCondition.MinorFault, name: "Minor Fault" },
            { id: EngineCondition.MajorFault, name: "Major Fault" },
          ]}
          setValue={setValue}
          watch={watch}
        />

        <SelectInput
          label="Body Condition"
          register={register}
          registerName={"bodyCondition"}
          options={[
            { id: BodyCondition.Good       , name: "Good" },
            { id: BodyCondition.Average    , name: "Average" },
            { id: BodyCondition.HeavyDamage, name: "HeavyDamage" },
          ]}
          setValue={setValue}
          watch={watch}
        />
   
        <div className="col-span-1 sm:col-span-2">
          <TextInput
            label="Mileage"
            placeholder="Leave blank to use vehicle data"
            type="number"
            register={register}
            registerName={"mileage"}
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between col-span-1 sm:col-span-2">
          <SwitchInput
            label="Alloy Wheels"
            register={register}
            registerName={"alloyWheels"}
            setValue={setValue}
            watch={watch}
          />

          <ButtonInput  
            label="Calculate Quote"
            isSubmit={true}
            classes=""
          />
        </div>

      </form>
    </Modal>
  );
}